import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setActiveTab } from '../redux/slices/appSlice';
import { setActiveSearch, setTrackQuery } from '../redux/slices/djpalSlice';
import { searchTrack } from '../redux/thunk/djpalThunk';
import TrackContextMenu from './TrackContextMenu';
import './RowActions.scss';

// Shared "…" overflow menu for track rows in the Manage/Prepare tabs.
// Wraps TrackContextMenu (kebab) and owns the "Search in Mixpedia" wiring.
// Each host tab keeps its own Play button and passes file-aware overrides:
//   - `onAddToQueue(track)`: the tab's existing add-to-queue handler (uses the
//     localhost audio URL, not the raw filesystem path).
//   - `onAddToPlaylist(track)` (optional): the tab's add-to-playlist handler
//     when it needs to preserve the real local `filePath` association.
//   - `desktopLike` (optional): surface the Like item on desktop too. All the
//     local-file tabs pass it (product call 2026-07: Like is available on every
//     track row, like Spotify's heart). Pass the best identity you have on the
//     `track` object — `djpalId` (catalog id) + `artwork` — so the like is
//     id-resolvable by the recs and shows a cover in the Likes view. A like
//     whose artist/title matches nothing in Mixpedia is stored but silently
//     skipped by the recs (verified: resolvers are SELECT-only, unmatched
//     likes seed nothing and consume no caps).
//   - `openAtPoint` ({ x, y, nonce } | null): forwarded to TrackContextMenu —
//     lets the host row's onContextMenu open THIS same menu at the cursor, so
//     right-click and the ⋯ button always show identical options.
//   - `onLocateFile` / `fileMissing`: missing-file affordances (see
//     TrackContextMenu).
//   - `analyticsFrom`: surface tag for the menu analytics events.
const RowActions = ({ track, onAddToQueue, onAddToPlaylist, onRemoveFromLibrary, removeLabel, desktopLike = false, openAtPoint = null, onLocateFile, fileMissing = false, analyticsFrom }) => {
    const dispatch = useDispatch();

    // Navigate to Explore → Mixpedia (Tracks) with the query prefilled, then
    // kick off the search. Order matters for back-navigation: set the 'track'
    // sub-tab and query BEFORE setActiveTab('djpal') so the nav-history snapshot
    // captured on that switch is already a clean 'track' marker. The subsequent
    // search result then REPLACES that marker instead of stacking a stray
    // 'artist' entry — so a single Back returns to the originating tab (e.g.
    // Compatibility) rather than an empty Mixpedia artists page. setActiveTab
    // already sets the 'explore' category, so no separate setActiveCategory
    // (which would push an extra landing-page entry).
    const searchInMixpedia = useCallback((e, t) => {
        const query = t.artist ? `${t.artist} - ${t.title}` : t.title;
        if (!query) return;
        dispatch(setActiveSearch('track'));
        dispatch(setTrackQuery(query));
        dispatch(setActiveTab('djpal'));
        setTimeout(() => {
            // djpalId is the CATALOG id (set by enrichment) — never t.id here:
            // on the local-file tabs t.id is the local SQLite row id.
            dispatch(searchTrack({ trackName: query, trackId: t.djpalId || null, variant: 'A', artworkUrl: t.artwork || t.artworkUrl || null }));
        }, 150);
    }, [dispatch]);

    return (
        <span className="row-actions">
            <TrackContextMenu
                track={track}
                onAddToQueue={onAddToQueue}
                onAddToPlaylist={onAddToPlaylist}
                onSearch={searchInMixpedia}
                onRemoveFromLibrary={onRemoveFromLibrary}
                removeLabel={removeLabel}
                desktopLike={desktopLike}
                openAtPoint={openAtPoint}
                onLocateFile={onLocateFile}
                fileMissing={fileMissing}
                analyticsFrom={analyticsFrom}
            />
        </span>
    );
};

export default RowActions;
